import { Component, inject, OnInit, DestroyRef, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BadgeComponent, ButtonComponent, CardComponent } from '@verticalbridge/ui-components';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { AppRolesService, AppRoles } from '../../core/auth/app-roles.service';
import { AuthService } from '@auth0/auth0-angular';
import { Router } from '@angular/router';
import { RequestAccessModalComponent } from '../../shared/components/request-access-modal/request-access-modal.component';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { RequestAccessService, AccessRequestStatus } from '../../core/services/request-access.service';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { combineLatest, filter, forkJoin, map, startWith, take, tap } from 'rxjs';
import { environment } from '@env/environment';
import {
  externalRedirectMatchesSource,
  hasAccessToExternalSource,
  isPartnerPortalHandoff,
  isVerticalWorksConfigured,
} from '../../core/auth/external-redirect';
import { PartnerPortalAccessStatus, VerticalWorksAccessStatus } from '../../core/models';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    RequestAccessModalComponent,
    FooterComponent,
    BadgeComponent,
    ButtonComponent,
    CardComponent,
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  private authService = inject(PortalAuthService);
  private appRolesService = inject(AppRolesService);
  private auth0 = inject(AuthService);
  private requestAccessService = inject(RequestAccessService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private router = inject(Router);


  isRedirecting = true;
  isProduction = environment.production;

  userName = 'User';
  userEmail = '';

  // Request Access Modal state
  isRequestModalOpen = false;
  requestModalAppName = '';
  requestModalAppType = '';

  // Track existing access requests
  siteLocatorRequestStatus: AccessRequestStatus = { hasRequested: false };
  isSiteLocatorStatusLoading = true;
  viRequestStatus: AccessRequestStatus = { hasRequested: false };
  isViStatusLoading = true;

  isPartnerPortalTileResolved = false;
  isPartnerPortalTileHidden = false;

  isRolesLoading = true;
  // Set once Partner Portal's backend check has answered; the PP redirect
  // decisions in ngOnInit wait on it.
  private readonly isPartnerPortalConfirmed = signal(false);
  private readonly isPartnerPortalConfirmed$ = toObservable(this.isPartnerPortalConfirmed);
  private isRedirectIssued = false;

  appRoles: AppRoles = {
    colo: { hasAccess: false, roles: [] },
    siteLocator: { hasAccess: false, roles: [] },
    verticalIntel: { hasAccess: false, roles: [] },
    dueDiligence: { hasAccess: false, roles: [] },
    partnerPortal: { hasAccess: false, roles: [] },
    verticalWorks: { hasAccess: false, roles: [] }
  };

  // Vertical Works is the one tile that branches by population: internal
  // addresses get it disabled with no request path, because internal access
  // is a configured list on the Vertical Works instance that no form joins.
  get isVerticalWorksInternalUser(): boolean {
    return this.userEmail.toLowerCase().endsWith('@verticalbridge.com');
  }

  // Not deployed to production yet — same treatment as the Vertical Intel
  // tile above, where both Vertical Works URLs are empty.
  //
  // Gated on the same predicate as the status call and the access request
  // (isVerticalWorksConfigured, which needs both URLs) rather than on
  // verticalWorksUrl alone. Two switches for one fact drifted silently:
  // filling only the site URL left a visible tile whose status call never
  // ran, so hasAccess stayed false and every external user was offered a
  // Request Access that posted to the Portal's own origin.
  get isVerticalWorksTileHidden(): boolean {
    return !isVerticalWorksConfigured();
  }

  // A user whose Vertical Works account was disabled is neither a
  // click-through nor a request candidate: re-requesting would either be
  // rejected or silently re-open the account.
  get isVerticalWorksDisabled(): boolean {
    return this.appRoles.verticalWorks.status === VerticalWorksAccessStatus.Disabled;
  }

  // The status check never answered, so the tile has no standing to offer
  // Request Access — the button would post to the API that just failed. A
  // suspended vendor read as "no access, please request" this way: the API
  // was down, the failure collapsed to hasAccess: false, and the tile invited
  // them to re-request an account they already have.
  get isVerticalWorksStatusUnknown(): boolean {
    return this.appRoles.verticalWorks.statusUnknown === true;
  }

  get isPartnerPortalPending(): boolean {
    return !this.appRoles.partnerPortal.hasAccess
      && this.appRoles.partnerPortal.status === PartnerPortalAccessStatus.Pending;
  }

  // Same treatment as Vertical Works: a check that did not answer must not
  // offer Request Access, which would re-send an existing request.
  get isPartnerPortalStatusUnknown(): boolean {
    return this.appRoles.partnerPortal.statusUnknown === true;
  }

  ngOnInit(): void {
    // First load roles from token for immediate display
    const authMethod = localStorage.getItem('authMethod');
    this.appRolesService.reloadRoles();

    // Get user info - prioritize JWT token for direct login
    const tokenInfo = this.authService.getUserInfo();

    if (authMethod === 'direct' && tokenInfo) {
      this.userName = tokenInfo.name || tokenInfo.nickname || tokenInfo.email || 'User';
      this.userEmail = tokenInfo.email || '';
      this.hydrateUserDataOrFailsafe();
    } else {
      combineLatest([this.auth0.isLoading$, this.auth0.user$]).pipe(
        filter(([loading]) => !loading),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(([, user]) => {
        if (user && user.email) {
          this.userName = user.name || user.nickname || user.email || 'User';
          this.userEmail = user.email || '';
        } else if (tokenInfo) {
          this.userName = tokenInfo.name || tokenInfo.nickname || tokenInfo.email || 'User';
          this.userEmail = tokenInfo.email || '';
        }
        this.hydrateUserDataOrFailsafe();
      });
    }

    // Listen for user profile updates (from /userinfo fetch after direct login)
    this.authService.userProfile$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(profile => {
      if (profile && (profile.name || profile.nickname)) {
        this.userName = profile.name || profile.nickname || profile.email || 'User';
        this.userEmail = profile.email || this.userEmail;
        this.cdr.detectChanges();
      }
    });

    // Get app roles. Cache the values, but DO NOT clear isRolesLoading here
    // even when isRefreshing() is currently false. The initial getRoles()
    // emission delivers token-cached roles which can incorrectly show
    // `hasAccess: true` for apps the user has lost access to since the token
    // was issued (or where the token's roles drift from backend reality).
    // The template-side guard must wait for the authoritative backend
    // refresh, which sets isRolesLoading=false in refreshRolesFromBackendApis().
    this.appRolesService.getRoles().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(roles => {
      this.appRoles = roles;
      this.cdr.detectChanges();
    });

    // Auto-redirect non-VB users with Partner Portal access
    // For direct login, use JWT token; for social login, use auth0.user$
    const email$ = this.auth0.user$.pipe(
      startWith(null),
      map(user => {
        // For direct login, always prioritize JWT token
        const currentAuthMethod = localStorage.getItem('authMethod');
        const jwtInfo = this.authService.getUserInfo();

        if (currentAuthMethod === 'direct' && jwtInfo?.email) {
          return jwtInfo.email;
        }

        // For social login, use Auth0 SDK user
        if (user?.email) return user.email;

        // Fallback to JWT token
        return jwtInfo?.email || null;
      }),
      filter((email): email is string => !!email)
    );

    // Sending a user to Partner Portal waits for the backend PP check alone:
    // the first roles emission comes from the token, which can still grant
    // PP to a user whose request is pending (a token minted before the
    // pending rule) or whose role was removed after login. Staying on the
    // dashboard is decided straight away, so only users about to be sent to
    // PP wait. Other apps' handoffs are not held.
    combineLatest([
      email$,
      this.appRolesService.getRoles(),
      this.isPartnerPortalConfirmed$
    ]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(([email, roles, isPartnerPortalConfirmed]) => {
      // Roles keep arriving after a redirect is issued (the other apps'
      // checks land later). Deciding again would overwrite the navigation,
      // e.g. replace a PP deep link with the PP home page.
      if (this.isRedirectIssued) {
        return;
      }
      const lowerEmail = email.toLowerCase();

      // Cross-system handoff. Only redirect back to the source app if the
      // freshly-authenticated user actually has access to it. New signups
      // and existing accounts lacking the role land on the Portal dashboard
      // instead — they can request access from the appropriate tile.
      //
      // When `externalSource` is missing (legacy Partner Portal handoff
      // that only sets `redirectUrl`), the Partner Portal rule applies.
      const externalRedirect = localStorage.getItem('externalRedirectUrl');
      const externalSource = localStorage.getItem('externalRedirectSource');
      if (externalRedirect) {
        const hasAccessToSource = hasAccessToExternalSource(externalSource, roles);

        // SECURITY: belt-and-braces check that the redirect target's
        // origin matches both the declared source AND a known VB app URL.
        // AppComponent's constructor already filters at capture-time, but
        // re-validating here covers stored values that pre-date this
        // commit and prevents leaking an access token to a non-VB origin.
        // Both this and the capture-time gate read the one app table in
        // core/auth/external-redirect.ts, so adding an app cannot light one
        // and miss the other.
        const originMatchesSource = externalRedirectMatchesSource(externalSource, externalRedirect);

        if (hasAccessToSource && originMatchesSource) {
          if (isPartnerPortalHandoff(externalSource, externalRedirect) && !isPartnerPortalConfirmed) {
            this.isRedirecting = true;
            return;
          }

          // Loop guard: if we already redirected this tab to this source
          // in the last 10 s, render the dashboard instead of bouncing
          // again. Protects against the source app (e.g. OnlineColo)
          // failing to consume the handoff and shipping the user back
          // here with fresh ?source=&returnUrl= params, which would
          // otherwise restart the cycle.
          const lastRedirectKey = `lastPortalRedirect:${externalSource ?? 'partnerPortal'}`;
          const lastRedirectAt = Number(sessionStorage.getItem(lastRedirectKey) ?? '0');
          if (lastRedirectAt > 0 && Date.now() - lastRedirectAt < 10_000) {
            localStorage.removeItem('externalRedirectUrl');
            localStorage.removeItem('externalRedirectSource');
            this.isRedirecting = false;
            this.cdr.detectChanges();
            return;
          }

          localStorage.removeItem('externalRedirectUrl');
          localStorage.removeItem('externalRedirectSource');
          sessionStorage.setItem(lastRedirectKey, Date.now().toString());
          this.isRedirecting = true;
          this.cdr.detectChanges();
          const token = this.authService.getToken();
          const url = token
            ? `${externalRedirect}${externalRedirect.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
            : externalRedirect;
          this.isRedirectIssued = true;
          this.redirectTo(url);
          return;
        }

        // Authenticated but no access, or the redirect target doesn't
        // match the source app's origin. Drop the stale redirect markers
        // and fall through to render the dashboard.
        localStorage.removeItem('externalRedirectUrl');
        localStorage.removeItem('externalRedirectSource');
      }

      // Default home behavior (no `externalRedirectUrl` was stored, or it
      // was dropped above for failing the access/origin checks): non-VB
      // users with approved Partner Portal access auto-redirect to PP. VB
      // users and external users without PP access, including those whose
      // PP request is still pending, render the dashboard.
      if (lowerEmail.endsWith('@verticalbridge.com') || !roles.partnerPortal.hasAccess) {
        this.isRedirecting = false;
        return;
      }

      this.isRedirecting = true;
      this.cdr.detectChanges();
      if (!isPartnerPortalConfirmed) {
        return;
      }
      this.isRedirectIssued = true;
      this.navigateToPartnerPortal();
    });
  }

  navigateToColo(): void {
    if (!this.appRoles.colo.hasAccess) {
      return;
    }

    if (this.userEmail.toLowerCase().endsWith('@verticalbridge.com')) {
      this.redirectToColoWithToken();
      return;
    }

    this.requestAccessService.getColoUserData().pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (user) => {
        const isComplete = user?.isRegistrationComplete ?? (!!user?.firstName && !!user?.company);
        if (!user || !isComplete) {
          this.router.navigate(['/register'], { queryParams: { app: 'colo' } });
          return;
        }
        this.redirectToColoWithToken();
      },
      error: () => {
        this.router.navigate(['/register'], { queryParams: { app: 'colo' } });
      },
    });
  }

  private redirectToColoWithToken(): void {
    window.location.href = this.requestAccessService.buildColoHandoffUrl();
  }

  navigateToSiteLocator(): void {
    if (this.appRoles.siteLocator.hasAccess) {
      const token = this.authService.getToken();
      const baseUrl = environment.siteLocatorUrl;
      window.location.href = token
        ? `${baseUrl}?token=${encodeURIComponent(token)}`
        : baseUrl;
    }
  }

  navigateToVerticalIntel(): void {
    if (this.appRoles.verticalIntel.hasAccess) {
      const token = this.authService.getToken();
      const baseUrl = environment.verticalIntelUrl;

      if (token) {
        const url = `${baseUrl}?token=${encodeURIComponent(token)}`;
        window.location.href = url;
      } else {
        window.location.href = baseUrl;
      }
    }
  }

  navigateToPartnerPortal(): void {
    if (this.appRoles.partnerPortal.hasAccess) {
      const token = this.authService.getToken();
      const baseUrl = environment.partnerPortalUrl;
      this.redirectTo(token ? `${baseUrl}?token=${encodeURIComponent(token)}` : baseUrl);
    }
  }

  // Seam for the redirect decisions in ngOnInit, so specs can spy on it.
  protected redirectTo(url: string): void {
    window.location.href = url;
  }

  navigateToVerticalWorks(): void {
    if (this.appRoles.verticalWorks.hasAccess) {
      const token = this.authService.getToken();
      const baseUrl = environment.verticalWorksUrl;

      if (token) {
        window.location.href = `${baseUrl}?token=${encodeURIComponent(token)}`;
      } else {
        window.location.href = baseUrl;
      }
    }
  }

  logout(): void {
    this.authService.logout();
  }

  openRequestAccessModal(appName: string, appType: string): void {
    this.requestModalAppName = appName;
    this.requestModalAppType = appType;
    this.isRequestModalOpen = true;
  }

  private readonly APP_LABELS: Record<string, string> = {
    colo: 'Online Colo',
    siteLocator: 'Site Locator',
    verticalIntel: 'Vertical Intel',
    partnerPortal: 'Partner Portal',
    verticalWorks: 'Vertical Works',
  };

  isTileClickable(appType: 'colo' | 'siteLocator' | 'verticalIntel' | 'partnerPortal' | 'verticalWorks'): boolean {
    if (this.isRolesLoading) return false;
    if (appType === 'siteLocator' && this.isSiteLocatorStatusLoading) return false;
    if (appType === 'verticalIntel' && this.isViStatusLoading) return false;

    const role = this.appRoles[appType];
    if (role?.hasAccess) return true;

    if (appType === 'verticalIntel') {
      if (role?.status === 'revoked') return false;
      if (this.viRequestStatus.hasRequested && this.viRequestStatus.status !== 'Approved') return false;
      return true;
    }
    if (appType === 'siteLocator') {
      if (this.siteLocatorRequestStatus.hasRequested && this.siteLocatorRequestStatus.status !== 'UNLOCKED') return false;
      return true;
    }
    if (appType === 'partnerPortal') {
      // A pending request waits on a PP admin; re-requesting only re-sends
      // the admin email.
      return !this.isPartnerPortalPending && !this.isPartnerPortalStatusUnknown;
    }
    if (appType === 'verticalWorks') {
      // A disabled account has nothing to offer either population, so the
      // tile stops being clickable — mirrors the 'revoked' guard on Vertical
      // Intel above. Internal users get a disabled tile regardless: their
      // access is configured on the Vertical Works instance, so there is
      // nothing to click. External users without access open the request
      // modal.
      if (role?.status === VerticalWorksAccessStatus.Disabled) return false;
      // Same dead end as disabled: with the check unanswered, clicking the
      // tile would open a Request Access modal that posts to the API that
      // just failed.
      if (role?.statusUnknown === true) return false;
      return !this.isVerticalWorksInternalUser;
    }
    // Online Colo has no Request Access flow; a card without access is not clickable.
    return false;
  }

  onTileClick(appType: 'colo' | 'siteLocator' | 'verticalIntel' | 'partnerPortal' | 'verticalWorks'): void {
    if (!this.isTileClickable(appType)) return;

    const role = this.appRoles[appType];
    if (role?.hasAccess) {
      switch (appType) {
        case 'colo': return this.navigateToColo();
        case 'siteLocator': return this.navigateToSiteLocator();
        case 'verticalIntel': return this.navigateToVerticalIntel();
        case 'partnerPortal': return this.navigateToPartnerPortal();
        case 'verticalWorks': return this.navigateToVerticalWorks();
      }
    }

    this.openRequestAccessModal(this.APP_LABELS[appType] ?? appType, appType);
  }

  closeRequestAccessModal(): void {
    this.isRequestModalOpen = false;
    this.requestModalAppName = '';
    this.requestModalAppType = '';
  }

  onRequestSubmitted(): void {
    // Refresh the request status for the relevant app
    if (this.requestModalAppType === 'verticalIntel') {
      this.checkExistingViRequest();
    } else if (this.requestModalAppType === 'siteLocator') {
      this.checkExistingSiteLocatorRequest();
    }

    // Refresh roles directly from backend APIs (bypasses Auth0 token)
    // This is needed because Auth0 post-login actions only run on login, not token refresh
    if (this.userEmail) {
      this.isRolesLoading = true;
      this.appRolesService.refreshRolesFromBackend(this.userEmail).pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (roles) => {
          this.appRoles = roles;
          this.isRolesLoading = false;
          this.cdr.detectChanges();
        },
        error: () => {
          this.isRolesLoading = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  private checkExistingSiteLocatorRequest(): void {
    this.isSiteLocatorStatusLoading = true;
    this.requestAccessService.checkExistingRequest(this.userEmail).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(status => {
      this.siteLocatorRequestStatus = status;
      this.isSiteLocatorStatusLoading = false;
    });
  }

  private checkColoApplicationCount(): void {
    this.requestAccessService.getColoApplicationCount(this.userEmail).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(count => {
      this.isPartnerPortalTileHidden = count > 0;
      this.isPartnerPortalTileResolved = true;
      this.cdr.detectChanges();
    });
  }

  private hydrateUserDataOrFailsafe(): void {
    if (!this.userEmail) {
      // No address, no backend refresh: release the redirect gate on the
      // token's roles rather than hold the dashboard hidden.
      this.isPartnerPortalConfirmed.set(true);
      this.isPartnerPortalTileResolved = true;
      this.cdr.detectChanges();
      return;
    }
    this.checkExistingSiteLocatorRequest();
    this.checkExistingViRequest();
    this.refreshRolesFromBackendApis();
    if (this.userEmail.toLowerCase().endsWith('@verticalbridge.com')) {
      this.isPartnerPortalTileResolved = true;
      this.cdr.detectChanges();
    } else {
      this.checkColoApplicationCount();
    }
  }

  private checkExistingViRequest(): void {
    this.isViStatusLoading = true;
    this.requestAccessService.checkExistingViRequest(this.userEmail).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (status) => {
        this.viRequestStatus = status;
        this.isViStatusLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isViStatusLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private refreshRolesFromBackendApis(): void {
    this.isRolesLoading = true;
    // PP is checked on its own so the redirect waits only on PP, not on the
    // slowest of the other apps' checks. The tiles wait for both.
    const partnerPortal$ = this.appRolesService.refreshPartnerPortalFromBackend(this.userEmail).pipe(
      tap({
        next: () => this.isPartnerPortalConfirmed.set(true),
        error: () => this.isPartnerPortalConfirmed.set(true),
      })
    );
    const otherApps$ = this.appRolesService.refreshRolesFromBackend(this.userEmail, { isPartnerPortalIncluded: false });

    forkJoin([partnerPortal$, otherApps$]).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.appRoles = this.appRolesService.getCurrentRoles();
        this.isRolesLoading = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isRolesLoading = false;
        this.cdr.detectChanges();
      }
    });
  }
}
